/** Search evidence only. Never use this result to overwrite a price or recipe. */
export interface LibraryRecognitionTask {
  id: string; name: string; description?: string | null; unit: string;
  rateTypical: string | number;
  source?: string;
}
const words = (name: string) => new Set(name.toLowerCase()
  .replace(/\bexcavat(?:ion|e|ing)\b/g, "excavate")
  .replace(/\b(?:formation|forming)\b/g, "form")
  .replace(/\b(?:installation|installing|fitting)\b/g, "install")
  .split(/[^a-z]+/).map(w => w.length > 4 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w)
  .filter(w => w.length > 2 && !["the", "and", "supply", "install", "work", "existing", "new"].includes(w)));
const unitKey = (unit: string) => {
  const value = unit.toLowerCase().trim();
  if (["each", "no", "no.", "item", "nr"].includes(value)) return "each";
  if (["sqm", "m2", "m²"].includes(value)) return "area";
  if (["linear_m", "lm", "m", "lin m"].includes(value)) return "length";
  return value;
};
export function findLibraryRecognition<T extends LibraryRecognitionTask>(
  item: { name: string; unit: string }, tasks: T[],
): T | undefined {
  const wanted = words(item.name);
  if (!wanted.size) return undefined;
  return tasks.map(task => {
    const actual = words(task.name);
    const common = [...wanted].filter(word => actual.has(word)).length;
    return { task, score: unitKey(item.unit) === unitKey(task.unit)
      ? common / Math.max(wanted.size, actual.size) : 0 };
  }).filter(match => match.score >= 0.45)
    .sort((a, b) => b.score - a.score)[0]?.task;
}

/** Stable visual grouping only: source array, indices and prices stay untouched. */
export function groupQuoteRows<T>(rows: T[], group: (row: T) => string | null): T[] {
  const groups = new Map<string | null, T[]>();
  for (const row of rows) {
    const key = group(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }
  return [...groups.values()].flat();
}
